"use client";

import { FC, useMemo } from "react";
import { SportTypes } from "@finbackoffice/fe-core";
import { TranslationScopes, AssetsDirectory } from "@finbackoffice/enums";
import Translate from "components/base/translate/Translate";
import { Svg } from "components/base/svg/Svg";
import SportIcon from "components/base/sport-icon/SportIcon";
import styles from "../../policies.module.sass";

const BetTypesAndRules: FC = () => {
    return (
        <>
            <div className={styles.subHeader} id="bet_types">
                <Svg
                    src={`/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/policies-bet-types.svg`}
                    className="svg-sport-icon"
                    loader
                />
                <h2>
                    <Translate
                        tid="policies_sportsbook_betTypes"
                        namespace={TranslationScopes.PoliciesSportsbook}
                    />
                </h2>
            </div>
            <p>
                <b>
                    <Translate
                        tid="policies_sportsbook_types_heading"
                        namespace={TranslationScopes.PoliciesSportsbook}
                    />
                </b>
            </p>
            <Translate
                tid="policies_sportsbook_types_info"
                namespace={TranslationScopes.PoliciesSportsbook}
                dangerous>
                <div />
            </Translate>

            <p>
                <b>
                    <Translate
                        tid="policies_sportsbook_rules_heading"
                        namespace={TranslationScopes.PoliciesSportsbook}
                    />
                </b>
            </p>
            <Translate
                tid="policies_sportsbook_rules_info"
                namespace={TranslationScopes.PoliciesSportsbook}
                dangerous>
                <div />
            </Translate>
        </>
    );
};

const SettlementAndCancellation: FC = () => (
    <>
        <div className={styles.subHeader} id="settlement_cancellation">
            <Svg
                src={`/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/policies-settlement-cancellation.svg`}
                className="svg-sport-icon"
                loader
            />
            <h2>
                <Translate
                    tid="policies_sportsbook_settlement"
                    namespace={TranslationScopes.PoliciesSportsbook}
                />
            </h2>
        </div>
        <Translate
            tid="policies_sportsbook_settlement_mainInfo"
            namespace={TranslationScopes.PoliciesSportsbook}
            dangerous>
            <div />
        </Translate>

        <h3>
            <b>
                <Translate
                    tid="policies_sportsbook_settlement_thirdParty_heading"
                    namespace={TranslationScopes.PoliciesSportsbook}
                />
            </b>
        </h3>
        <p>
            <Translate
                tid="policies_sportsbook_settlement_thirdParty_info"
                namespace={TranslationScopes.PoliciesSportsbook}
            />
        </p>

        <h3 style={{ marginTop: 12 }}>
            <b>
                <Translate
                    tid="policies_sportsbook_settlement_outrightMarkets_heading"
                    namespace={TranslationScopes.PoliciesSportsbook}
                />
            </b>
        </h3>
        <p>
            <Translate
                tid="policies_sportsbook_settlement_outrightMarkets_paragraph1"
                namespace={TranslationScopes.PoliciesSportsbook}
            />
        </p>
        <p>
            <Translate
                tid="policies_sportsbook_settlement_outrightMarkets_paragraph2"
                namespace={TranslationScopes.PoliciesSportsbook}
            />
        </p>
        <p>
            <Translate
                tid="policies_sportsbook_settlement_outrightMarkets_paragraph3"
                namespace={TranslationScopes.PoliciesSportsbook}
            />
        </p>
    </>
);

const Soccer: FC = () => (
    <>
        <div className={styles.subHeader} id={`${SportTypes.SOCCER}`}>
            <SportIcon sportId={SportTypes.SOCCER} />
            <h2>
                <Translate tid="sport_soccer" />
            </h2>
        </div>
        <p>
            <b>
                <Translate
                    tid="policies_sportsbook_settlement_heading"
                    namespace={TranslationScopes.PoliciesSportsbook}
                />
            </b>
        </p>
        <Translate
            tid="policies_sportsbook_soccer_settlement_info"
            namespace={TranslationScopes.PoliciesSportsbook}
            dangerous>
            <div />
        </Translate>

        <p>
            <b>
                <Translate
                    tid="policies_sportsbook_soccer_bookingMarkets_heading"
                    namespace={TranslationScopes.PoliciesSportsbook}
                />
            </b>
        </p>
        <Translate
            tid="policies_sportsbook_soccer_bookingMarkets_info1"
            namespace={TranslationScopes.PoliciesSportsbook}
            dangerous>
            <div />
        </Translate>

        <p>
            <Translate
                tid="policies_sportsbook_soccer_bookingMarkets_paragraph1"
                namespace={TranslationScopes.PoliciesSportsbook}
            />
        </p>
        <Translate
            tid="policies_sportsbook_soccer_bookingMarkets_info2"
            namespace={TranslationScopes.PoliciesSportsbook}
            dangerous>
            <div />
        </Translate>
        <p>
            <Translate
                tid="policies_sportsbook_soccer_bookingMarkets_paragraph2"
                namespace={TranslationScopes.PoliciesSportsbook}
            />
        </p>
        <Translate
            tid="policies_sportsbook_soccer_bookingMarkets_info3"
            namespace={TranslationScopes.PoliciesSportsbook}
            dangerous>
            <div />
        </Translate>

        <p>
            <b>
                <Translate
                    tid="policies_sportsbook_soccer_cornerMarkets_heading"
                    namespace={TranslationScopes.PoliciesSportsbook}
                />
            </b>
        </p>
        <ul>
            <li>
                <Translate
                    tid="policies_sportsbook_soccer_cornerMarkets_info"
                    namespace={TranslationScopes.PoliciesSportsbook}
                />
            </li>
        </ul>

        <p>
            <b>
                <Translate
                    tid="policies_sportsbook_soccer_nextGoalscorer_heading"
                    namespace={TranslationScopes.PoliciesSportsbook}
                />
            </b>
        </p>
        <Translate
            tid="policies_sportsbook_soccer_nextGoalscorer_info"
            namespace={TranslationScopes.PoliciesSportsbook}
            dangerous>
            <div />
        </Translate>

        <p>
            <b>
                <Translate
                    tid="policies_sportsbook_soccer_anytimeGoalscorer_heading"
                    namespace={TranslationScopes.PoliciesSportsbook}
                />
            </b>
        </p>
        <Translate
            tid="policies_sportsbook_soccer_anytimeGoalscorer_info"
            namespace={TranslationScopes.PoliciesSportsbook}
            dangerous>
            <div />
        </Translate>

        <p>
            <b>
                <Translate
                    tid="policies_sportsbook_soccer_intervalMarkets_heading"
                    namespace={TranslationScopes.PoliciesSportsbook}
                />
            </b>
        </p>
        <Translate
            tid="policies_sportsbook_soccer_intervalMarkets_info"
            namespace={TranslationScopes.PoliciesSportsbook}
            dangerous>
            <div />
        </Translate>

        <p>
            <b>
                <Translate
                    tid="policies_sportsbook_soccer_nextScoring_heading"
                    namespace={TranslationScopes.PoliciesSportsbook}
                />
            </b>
        </p>
        <Translate
            tid="policies_sportsbook_soccer_nextScoring_info"
            namespace={TranslationScopes.PoliciesSportsbook}
            dangerous>
            <div />
        </Translate>
    </>
);

const Tennis: FC = () => (
    <>
        <div className={styles.subHeader} id={`${SportTypes.TENNIS}`}>
            <SportIcon sportId={SportTypes.TENNIS} />
            <h2>
                <Translate tid="sport_tennis" />
            </h2>
        </div>
        <p>
            <b>
                <Translate
                    tid="policies_sportsbook_settlement_heading"
                    namespace={TranslationScopes.PoliciesSportsbook}
                />
            </b>
        </p>
        <Translate
            tid="policies_sportsbook_tennis_rules_info"
            dangerous
            namespace={TranslationScopes.PoliciesSportsbook}>
            <div />
        </Translate>
    </>
);

const Basketball: FC = () => (
    <>
        <div className={styles.subHeader} id={`${SportTypes.BASKETBALL}`}>
            <SportIcon sportId={SportTypes.BASKETBALL} />
            <h2>
                <Translate tid="sport_basketball" />
            </h2>
        </div>

        <p>
            <b>
                <Translate
                    tid="policies_sportsbook_settlement_heading"
                    namespace={TranslationScopes.PoliciesSportsbook}
                />
            </b>
        </p>
        <Translate
            tid="policies_sportsbook_basketball_rules_info"
            dangerous
            namespace={TranslationScopes.PoliciesSportsbook}>
            <div />
        </Translate>
    </>
);

const AmericanFootball: FC = () => (
    <>
        <div className={styles.subHeader} id={`${SportTypes.AMERICAN_FOOTBALL}`}>
            <SportIcon sportId={SportTypes.AMERICAN_FOOTBALL} />
            <h2>
                <Translate tid="sport_american_football" />
            </h2>
        </div>
        <p>
            <b>
                <Translate
                    tid="policies_sportsbook_settlement_heading"
                    namespace={TranslationScopes.PoliciesSportsbook}
                />
            </b>
        </p>
        <Translate
            tid="policies_sportsbook_american_football_rules_info"
            dangerous
            namespace={TranslationScopes.PoliciesSportsbook}>
            <div />
        </Translate>
        <p>
            <b>
                <Translate
                    tid="policies_sportsbook_american_football_player_props_heading"
                    namespace={TranslationScopes.PoliciesSportsbook}
                />
            </b>
        </p>
        <Translate
            tid="policies_sportsbook_american_football_player_props_info"
            dangerous
            namespace={TranslationScopes.PoliciesSportsbook}>
            <div />
        </Translate>
    </>
);

const IceHockey: FC = () => (
    <>
        <div className={styles.subHeader} id={`${SportTypes.ICE_HOCKEY}`}>
            <SportIcon sportId={SportTypes.ICE_HOCKEY} />
            <h2>
                <Translate tid="sport_ice_hockey" />
            </h2>
        </div>
        <p>
            <b>
                <Translate
                    tid="policies_sportsbook_settlement_heading"
                    namespace={TranslationScopes.PoliciesSportsbook}
                />
            </b>
        </p>
        <Translate
            tid="policies_sportsbook_ice_hockey_rules_info"
            dangerous
            namespace={TranslationScopes.PoliciesSportsbook}>
            <div />
        </Translate>
        <p>
            <b>
                <Translate
                    tid="policies_sportsbook_ice_hockey_heading_two"
                    namespace={TranslationScopes.PoliciesSportsbook}
                />
            </b>
        </p>
        <Translate
            tid="policies_sportsbook_ice_hockey_heading_two_info"
            dangerous
            namespace={TranslationScopes.PoliciesSportsbook}>
            <div />
        </Translate>
        <p>
            <b>
                <Translate
                    tid="policies_sportsbook_ice_hockey_next_scoring_heading"
                    namespace={TranslationScopes.PoliciesSportsbook}
                />
            </b>
        </p>
        <Translate
            tid="policies_sportsbook_ice_hockey_next_scoring_info"
            dangerous
            namespace={TranslationScopes.PoliciesSportsbook}>
            <div />
        </Translate>
        <p>
            <b>
                <Translate
                    tid="policies_sportsbook_ice_hockey_player_props_heading"
                    namespace={TranslationScopes.PoliciesSportsbook}
                />
            </b>
        </p>
        <Translate
            tid="policies_sportsbook_ice_hockey_player_props_info"
            dangerous
            namespace={TranslationScopes.PoliciesSportsbook}>
            <div />
        </Translate>
    </>
);

const Baseball: FC = () => (
    <>
        <div className={styles.subHeader} id={`${SportTypes.BASEBALL}`}>
            <SportIcon sportId={SportTypes.BASEBALL} />
            <h2>
                <Translate tid="sport_baseball" />
            </h2>
        </div>
        <p>
            <b>
                <Translate
                    tid="policies_sportsbook_settlement_heading"
                    namespace={TranslationScopes.PoliciesSportsbook}
                />
            </b>
        </p>
        <Translate
            tid="policies_sportsbook_baseball_rules_info"
            dangerous
            namespace={TranslationScopes.PoliciesSportsbook}>
            <div />
        </Translate>
        <p>
            <b>
                <Translate
                    tid="policies_sportsbook_baseball_rapid_markets_heading"
                    namespace={TranslationScopes.PoliciesSportsbook}
                />
            </b>
        </p>
        <Translate
            tid="policies_sportsbook_baseball_rapid_markets_info"
            dangerous
            namespace={TranslationScopes.PoliciesSportsbook}>
            <div />
        </Translate>
        <p>
            <b>
                <Translate
                    tid="policies_sportsbook_baseball_player_props_heading"
                    namespace={TranslationScopes.PoliciesSportsbook}
                />
            </b>
        </p>
        <Translate
            tid="policies_sportsbook_baseball_player_props_info"
            dangerous
            namespace={TranslationScopes.PoliciesSportsbook}>
            <div />
        </Translate>
    </>
);

const Handball: FC = () => (
    <>
        <div className={styles.subHeader} id={`${SportTypes.HANDBALL}`}>
            <SportIcon sportId={SportTypes.HANDBALL} />
            <h2>
                <Translate tid="sport_handball" />
            </h2>
        </div>

        <p>
            <b>
                <Translate
                    tid="policies_sportsbook_settlement_heading"
                    namespace={TranslationScopes.PoliciesSportsbook}
                />
            </b>
        </p>

        <Translate
            tid="policies_sportsbook_handball_rules_info"
            dangerous
            namespace={TranslationScopes.PoliciesSportsbook}>
            <div />
        </Translate>
    </>
);

const Volleyball: FC = () => (
    <>
        <div className={styles.subHeader} id={`${SportTypes.VOLLEYBALL}`}>
            <SportIcon sportId={SportTypes.VOLLEYBALL} />
            <h2>
                <Translate tid="sport_volleyball" />
            </h2>
        </div>

        <p>
            <b>
                <Translate
                    tid="policies_sportsbook_settlement_heading"
                    namespace={TranslationScopes.PoliciesSportsbook}
                />
            </b>
        </p>

        <Translate
            tid="policies_sportsbook_volleyball_rules_info"
            dangerous
            namespace={TranslationScopes.PoliciesSportsbook}>
            <div />
        </Translate>
    </>
);

const Futsal: FC = () => (
    <>
        <div className={styles.subHeader} id={`${SportTypes.FUTSAL}`}>
            <SportIcon sportId={SportTypes.FUTSAL} />
            <h2>
                <Translate tid="sport_futsal" />
            </h2>
        </div>

        <p>
            <b>
                <Translate
                    tid="policies_sportsbook_settlement_heading"
                    namespace={TranslationScopes.PoliciesSportsbook}
                />
            </b>
        </p>

        <Translate
            tid="policies_sportsbook_futsal_rules_info"
            dangerous
            namespace={TranslationScopes.PoliciesSportsbook}>
            <div />
        </Translate>
    </>
);

const Badminton: FC = () => (
    <>
        <div className={styles.subHeader} id={`${SportTypes.BADMINTON}`}>
            <SportIcon sportId={SportTypes.BADMINTON} />
            <h2>
                <Translate tid="sport_badminton" />
            </h2>
        </div>

        <p>
            <b>
                <Translate
                    tid="policies_sportsbook_settlement_heading"
                    namespace={TranslationScopes.PoliciesSportsbook}
                />
            </b>
        </p>
        <Translate
            tid="policies_sportsbook_badminton_rules_info"
            dangerous
            namespace={TranslationScopes.PoliciesSportsbook}>
            <div />
        </Translate>
    </>
);

const Rugby: FC = () => (
    <>
        <div className={styles.subHeader} id={`${SportTypes.RUGBY}`}>
            <SportIcon sportId={SportTypes.RUGBY} />
            <h2>
                <Translate tid="sport_rugby" />
            </h2>
        </div>
        <h3>
            <b>
                <Translate
                    tid="policies_sportsbook_rugby_union_league"
                    namespace={TranslationScopes.PoliciesSportsbook}
                />
            </b>
        </h3>

        <p>
            <b>
                <Translate
                    tid="policies_sportsbook_settlement_heading"
                    namespace={TranslationScopes.PoliciesSportsbook}
                />
            </b>
        </p>
        <Translate
            tid="policies_sportsbook_rugby_rules_info"
            dangerous
            namespace={TranslationScopes.PoliciesSportsbook}>
            <div />
        </Translate>
        <h3>
            <b>
                <Translate
                    tid="policies_sportsbook_rugby_sevens"
                    namespace={TranslationScopes.PoliciesSportsbook}
                />
            </b>
        </h3>

        <p>
            <b>
                <Translate
                    tid="policies_sportsbook_settlement_heading"
                    namespace={TranslationScopes.PoliciesSportsbook}
                />
            </b>
        </p>
        <Translate
            tid="policies_sportsbook_rugby_sevens_info"
            dangerous
            namespace={TranslationScopes.PoliciesSportsbook}>
            <div />
        </Translate>
    </>
);

const Darts: FC = () => (
    <>
        <div className={styles.subHeader} id={`${SportTypes.DARTS}`}>
            <SportIcon sportId={SportTypes.DARTS} />
            <h2>
                <Translate tid="sport_darts" />
            </h2>
        </div>

        <p>
            <b>
                <Translate
                    tid="policies_sportsbook_settlement_heading"
                    namespace={TranslationScopes.PoliciesSportsbook}
                />
            </b>
        </p>

        <Translate
            tid="policies_sportsbook_darts_rules_info"
            dangerous
            namespace={TranslationScopes.PoliciesSportsbook}>
            <div />
        </Translate>
    </>
);

const Snooker: FC = () => (
    <>
        <div className={styles.subHeader} id={`${SportTypes.SNOOKER}`}>
            <SportIcon sportId={SportTypes.SNOOKER} />
            <h2>
                <Translate tid="sport_snooker" />
            </h2>
        </div>

        <p>
            <b>
                <Translate
                    tid="policies_sportsbook_settlement_heading"
                    namespace={TranslationScopes.PoliciesSportsbook}
                />
            </b>
        </p>

        <Translate
            tid="policies_sportsbook_snooker_rules_info"
            dangerous
            namespace={TranslationScopes.PoliciesSportsbook}>
            <div />
        </Translate>
    </>
);

const TableTennis: FC = () => (
    <>
        <div className={styles.subHeader} id={`${SportTypes.TABLE_TENNIS}`}>
            <SportIcon sportId={SportTypes.TABLE_TENNIS} />
            <h2>
                <Translate tid="sport_table_tennis" />
            </h2>
        </div>

        <p>
            <b>
                <Translate
                    tid="policies_sportsbook_settlement_heading"
                    namespace={TranslationScopes.PoliciesSportsbook}
                />
            </b>
        </p>
        <Translate
            tid="policies_sportsbook_table_tennis_rules_info"
            dangerous
            namespace={TranslationScopes.PoliciesSportsbook}>
            <div />
        </Translate>
    </>
);

const Bowls: FC = () => (
    <>
        <div className={styles.subHeader} id={`${SportTypes.BOWLS}`}>
            <SportIcon sportId={SportTypes.BOWLS} />
            <h2>
                <Translate tid="sport_bowls" />
            </h2>
        </div>
        <p>
            <b>
                <Translate
                    tid="policies_sportsbook_settlement_heading"
                    namespace={TranslationScopes.PoliciesSportsbook}
                />
            </b>
        </p>
        <Translate
            tid="policies_sportsbook_bowls_rules_info"
            dangerous
            namespace={TranslationScopes.PoliciesSportsbook}>
            <div />
        </Translate>
    </>
);

const Squash: FC = () => (
    <>
        <div className={styles.subHeader} id={`${SportTypes.SQUASH}`}>
            <SportIcon sportId={SportTypes.SQUASH} />
            <h2>
                <Translate tid="sport_squash" />
            </h2>
        </div>

        <p>
            <b>
                <Translate
                    tid="policies_sportsbook_settlement_heading"
                    namespace={TranslationScopes.PoliciesSportsbook}
                />
            </b>
        </p>

        <Translate
            tid="policies_sportsbook_squash_rules_info"
            dangerous
            namespace={TranslationScopes.PoliciesSportsbook}>
            <div />
        </Translate>
    </>
);

const AussieRules: FC = () => (
    <>
        <div className={styles.subHeader} id={SportTypes.AUSSIE_RULES.toString()}>
            <SportIcon sportId={SportTypes.AUSSIE_RULES} />
            <h2>
                <Translate tid="sport_aussie_rules" />
            </h2>
        </div>

        <p>
            <b>
                <Translate
                    tid="policies_sportsbook_settlement_heading"
                    namespace={TranslationScopes.PoliciesSportsbook}
                />
            </b>
        </p>
        <Translate
            tid="policies_sportsbook_aussie_rules_rules_info"
            dangerous
            namespace={TranslationScopes.PoliciesSportsbook}>
            <div />
        </Translate>
    </>
);

const FieldHockey: FC = () => (
    <>
        <div className={styles.subHeader} id={SportTypes.FIELD_HOCKEY.toString()}>
            <SportIcon sportId={SportTypes.FIELD_HOCKEY} />
            <h2>
                <Translate tid="sport_field_hockey" />
            </h2>
        </div>

        <p>
            <b>
                <Translate
                    tid="policies_sportsbook_settlement_heading"
                    namespace={TranslationScopes.PoliciesSportsbook}
                />
            </b>
        </p>

        <Translate
            tid="policies_sportsbook_field_hockey_rules_info"
            dangerous
            namespace={TranslationScopes.PoliciesSportsbook}>
            <div />
        </Translate>

        <h3>
            <b>
                <Translate
                    tid="policies_sportsbook_field_hockey_heading1"
                    namespace={TranslationScopes.PoliciesSportsbook}
                />
            </b>
        </h3>

        <p>
            <Translate
                tid="policies_sportsbook_field_hockey_info1"
                namespace={TranslationScopes.PoliciesSportsbook}
            />
        </p>
        <h3 style={{ marginTop: 12 }}>
            <b>
                <Translate
                    tid="policies_sportsbook_field_hockey_heading2"
                    namespace={TranslationScopes.PoliciesSportsbook}
                />
            </b>
        </h3>
        <Translate
            tid="policies_sportsbook_field_hockey_info2"
            dangerous
            namespace={TranslationScopes.PoliciesSportsbook}>
            <div />
        </Translate>

        <h3>
            <b>
                <Translate
                    tid="policies_sportsbook_field_hockey_heading3"
                    namespace={TranslationScopes.PoliciesSportsbook}
                />
            </b>
        </h3>
        <p>
            <Translate
                tid="policies_sportsbook_field_hockey_heading3_p1"
                namespace={TranslationScopes.PoliciesSportsbook}
            />
        </p>
        <p>
            <b>
                <Translate
                    tid="policies_sportsbook_field_hockey_heading3_p2"
                    namespace={TranslationScopes.PoliciesSportsbook}
                />
            </b>
        </p>
        <Translate
            tid="policies_sportsbook_field_hockey_info3"
            dangerous
            namespace={TranslationScopes.PoliciesSportsbook}>
            <div />
        </Translate>
        <p>
            <b>
                <Translate
                    tid="policies_sportsbook_field_hockey_heading4"
                    namespace={TranslationScopes.PoliciesSportsbook}
                />
            </b>
        </p>
        <Translate
            tid="policies_sportsbook_field_hockey_info4"
            dangerous
            namespace={TranslationScopes.PoliciesSportsbook}>
            <div />
        </Translate>
    </>
);

const Boxing: FC = () => (
    <>
        <div className={styles.subHeader} id={SportTypes.BOXING.toString()}>
            <SportIcon sportId={SportTypes.BOXING} />
            <h2>
                <Translate tid="sport_boxing" />
            </h2>
        </div>

        <p>
            <Translate
                tid="policies_sportsbook_boxing_p"
                namespace={TranslationScopes.PoliciesSportsbook}
            />
        </p>

        <h3 style={{ marginTop: 12 }}>
            <b>
                <Translate
                    tid="policies_sportsbook_boxing_heading"
                    namespace={TranslationScopes.PoliciesSportsbook}
                />
            </b>
        </h3>

        <Translate
            tid="policies_sportsbook_boxing_info"
            dangerous
            namespace={TranslationScopes.PoliciesSportsbook}>
            <div />
        </Translate>
    </>
);

const Formula1: FC = () => (
    <>
        <div className={styles.subHeader} id={SportTypes.FORMULA_1.toString()}>
            <SportIcon sportId={SportTypes.FORMULA_1} />
            <h2>
                <Translate tid="sport_formula1" />
            </h2>
        </div>

        <p>
            <b>
                <Translate
                    tid="policies_sportsbook_formula1_heading1"
                    namespace={TranslationScopes.PoliciesSportsbook}
                />
            </b>
        </p>
        <Translate
            tid="policies_sportsbook_formula1_info1"
            dangerous
            namespace={TranslationScopes.PoliciesSportsbook}>
            <div />
        </Translate>

        <p>
            <b>
                <Translate
                    tid="policies_sportsbook_formula1_heading2"
                    namespace={TranslationScopes.PoliciesSportsbook}
                />
            </b>
        </p>
        <Translate
            tid="policies_sportsbook_formula1_info2"
            dangerous
            namespace={TranslationScopes.PoliciesSportsbook}>
            <div />
        </Translate>

        <p>
            <b>
                <Translate
                    tid="policies_sportsbook_formula1_heading3"
                    namespace={TranslationScopes.PoliciesSportsbook}
                />
            </b>
        </p>
        <Translate
            tid="policies_sportsbook_formula1_info3"
            dangerous
            namespace={TranslationScopes.PoliciesSportsbook}>
            <div />
        </Translate>

        <p>
            <b>
                <Translate
                    tid="policies_sportsbook_formula1_heading4"
                    namespace={TranslationScopes.PoliciesSportsbook}
                />
            </b>
        </p>
        <Translate
            tid="policies_sportsbook_formula1_info4"
            dangerous
            namespace={TranslationScopes.PoliciesSportsbook}>
            <div />
        </Translate>

        <p>
            <b>
                <Translate
                    tid="policies_sportsbook_formula1_heading5"
                    namespace={TranslationScopes.PoliciesSportsbook}
                />
            </b>
        </p>
        <Translate
            tid="policies_sportsbook_formula1_info5"
            dangerous
            namespace={TranslationScopes.PoliciesSportsbook}>
            <div />
        </Translate>
        <p>
            <b>
                <Translate
                    tid="policies_sportsbook_formula1_heading6"
                    namespace={TranslationScopes.PoliciesSportsbook}
                />
            </b>
        </p>
        <Translate
            tid="policies_sportsbook_formula1_info6"
            dangerous
            namespace={TranslationScopes.PoliciesSportsbook}>
            <div />
        </Translate>

        <p>
            <b>
                <Translate
                    tid="policies_sportsbook_formula1_heading7"
                    namespace={TranslationScopes.PoliciesSportsbook}
                />
            </b>
        </p>
        <Translate
            tid="policies_sportsbook_formula1_info7"
            dangerous
            namespace={TranslationScopes.PoliciesSportsbook}>
            <div />
        </Translate>
    </>
);

type Props = {
    scope: string;
};

const PoliciesSportsbookContent: FC<Props> = ({ scope }) => {
    const renderContent = useMemo(() => {
        switch (scope[0]?.toUpperCase()?.replace(/-/g, "_")) {
            case "GENERAL_RULES":
                return <BetTypesAndRules />;
            case "SECONDARY_RULES":
                return <SettlementAndCancellation />;
            case "SOCCER":
                return <Soccer />;
            case "TENNIS":
                return <Tennis />;
            case "BASKETBALL":
                return <Basketball />;
            case "AMERICAN_FOOTBALL":
                return <AmericanFootball />;
            case "ICE_HOCKEY":
                return <IceHockey />;
            case "BASEBALL":
                return <Baseball />;
            case "HANDBALL":
                return <Handball />;
            case "VOLLEYBALL":
                return <Volleyball />;
            case "FUTSAL":
                return <Futsal />;
            case "BADMINTON":
                return <Badminton />;
            case "RUGBY":
                return <Rugby />;
            case "DARTS":
                return <Darts />;
            case "SNOOKER":
                return <Snooker />;
            case "TABLE_TENNIS":
                return <TableTennis />;
            case "BOWLS":
                return <Bowls />;
            case "SQUASH":
                return <Squash />;
            case "AUSSIE_RULES":
                return <AussieRules />;
            case "FIELD_HOCKEY":
                return <FieldHockey />;
            case "BOXING":
                return <Boxing />;
            case "FORMULA_1":
                return <Formula1 />;
            default:
                return null;
        }
    }, [scope]);

    return <>{renderContent}</>;
};

export default PoliciesSportsbookContent;
